/**
 * TanStack Query hooks for AuditLog resource (admin-only, read-only).
 *
 * Note: audit-logs endpoint returns a non-standard shape:
 *   { data: { items, total, page, pageSize, totalPages } }
 * api.get unwraps { data: T } → T, so we get { items, total, page, pageSize, totalPages } directly.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@client/lib/api-client';

export type AuditAction = 'create' | 'update' | 'delete' | 'login' | 'share';

export interface AuditLog {
  id: string;
  actorUserId: string;
  actorName?: string | null;
  action: AuditAction;
  entity: string;
  entityId?: string | null;
  branchId?: string | null;
  branchName?: string | null;
  meta?: Record<string, unknown> | null;
  createdAt: string;
}

export interface AuditLogPage {
  items: AuditLog[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ListAuditLogsParams {
  branchId?: string;
  actorUserId?: string;
  action?: AuditAction;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
}

export interface StaffActor {
  userId: string;
  fullName: string;
}

export const auditLogKeys = {
  all: ['audit-logs'] as const,
  list: (params?: ListAuditLogsParams) => ['audit-logs', 'list', params] as const,
  actors: (q?: string) => ['audit-logs', 'actors', q] as const,
};

export function useAuditLogs(params?: ListAuditLogsParams) {
  return useQuery({
    queryKey: auditLogKeys.list(params),
    // Endpoint returns { data: { items, total, page, pageSize, totalPages } }
    // api.get unwraps { data: T } → AuditLogPage
    queryFn: () =>
      api.get<AuditLogPage>(
        '/api/audit-logs',
        params as Record<string, string | number | boolean | undefined | null>,
      ),
  });
}

export function useAuditLogActors(q?: string) {
  return useQuery({
    queryKey: auditLogKeys.actors(q),
    // Endpoint returns { data: { items: StaffActor[] } } → api.get unwraps to { items }
    queryFn: () =>
      api.get<{ items: StaffActor[] }>('/api/audit-logs/actors', q ? { q } : undefined),
  });
}
